import { EMPTY_TEXT } from '@mitchell/react.component.ux';
import { IntelligentPhotoCaptureArtifactsCategoryNames, IntelligentPhotoCaptureEstimatorContentType, IntelligentPhotoCaptureVinOption, PhotoCaptureDownloadArtifactsLabelNames } from '../enums';
const getMaxConfidenceForContentTypes = (candidate, contentTypes) => {
    var _a;
    const candidateScores = (_a = candidate.attachment.foldersModelInfo) === null || _a === void 0 ? void 0 : _a.filter((folderModel) => !!folderModel.contentType && contentTypes.includes(folderModel.contentType)).map((folderModel) => { var _a; return (_a = folderModel.confidence) !== null && _a !== void 0 ? _a : 0; });
    if (!(candidateScores && candidateScores.length)) {
        return Number.NEGATIVE_INFINITY;
    }
    return candidateScores.reduce((maxScore, score) => Math.max(maxScore, score), Number.NEGATIVE_INFINITY);
};
const isVinOptionUnset = (vinOption) => vinOption !== IntelligentPhotoCaptureVinOption.VIN && vinOption !== IntelligentPhotoCaptureVinOption.VIN_STICKER;
export const shouldKeepVinAndVinStickerInVehicleInformation = (vinOption) => isVinOptionUnset(vinOption);
/**
 * Returns whether a given content type is VIN-like (VIN or VIN_STICKER).
 * @param contentType The content type to check.
 * @returns True when the content type is VIN or VIN_STICKER.
 */
export const isVinLikeContentType = (contentType) => contentType === IntelligentPhotoCaptureEstimatorContentType.VIN ||
    contentType === IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER;
export const getPreferredVinContentTypeByOption = (vinOption) => vinOption === IntelligentPhotoCaptureVinOption.VIN_STICKER
    ? IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER
    : IntelligentPhotoCaptureEstimatorContentType.VIN;
export const getVinLikeFolderTypeCodeByOption = (contentType, vinOption) => {
    if (shouldKeepVinAndVinStickerInVehicleInformation(vinOption)) {
        return IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION;
    }
    return contentType === getPreferredVinContentTypeByOption(vinOption)
        ? IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION
        : IntelligentPhotoCaptureArtifactsCategoryNames.ADDITIONAL_ARTIFACTS;
};
export const applyVinLikeCrossTypeDemotion = ({ flatAttachments, demoteMap, vinOption, vinNo, isVehicleInfoTextMatched }) => {
    const vinLikeContentTypes = [
        IntelligentPhotoCaptureEstimatorContentType.VIN,
        IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER
    ];
    const vinLikeCandidates = flatAttachments.filter((flat) => flat.attachment.foldersModelInfo &&
        flat.attachment.foldersModelInfo.some((folderModel) => folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
            !!folderModel.contentType &&
            vinLikeContentTypes.includes(folderModel.contentType)));
    const hasVinCandidate = vinLikeCandidates.some((candidate) => candidate.attachment.foldersModelInfo &&
        candidate.attachment.foldersModelInfo.some((folderModel) => folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
            folderModel.contentType === IntelligentPhotoCaptureEstimatorContentType.VIN));
    const hasVinStickerCandidate = vinLikeCandidates.some((candidate) => candidate.attachment.foldersModelInfo &&
        candidate.attachment.foldersModelInfo.some((folderModel) => folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
            folderModel.contentType === IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER));
    if (!(hasVinCandidate && hasVinStickerCandidate)) {
        return;
    }
    const preferredVinLikeContentType = vinOption === IntelligentPhotoCaptureVinOption.VIN_STICKER
        ? IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER
        : vinOption === IntelligentPhotoCaptureVinOption.VIN
            ? IntelligentPhotoCaptureEstimatorContentType.VIN
            : undefined;
    let vinWinnerPool = preferredVinLikeContentType
        ? vinLikeCandidates.filter((candidate) => candidate.attachment.foldersModelInfo &&
            candidate.attachment.foldersModelInfo.some((folderModel) => folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
                folderModel.contentType === preferredVinLikeContentType))
        : vinLikeCandidates;
    if (!preferredVinLikeContentType) {
        const userEditedPool = vinWinnerPool.filter((candidate) => candidate.attachment.foldersModelInfo &&
            candidate.attachment.foldersModelInfo.some((folderModel) => folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
                !!folderModel.contentType &&
                vinLikeContentTypes.includes(folderModel.contentType) &&
                !!folderModel.isImageEdited));
        if (userEditedPool.length) {
            vinWinnerPool = userEditedPool;
        }
        if (!!(vinNo === null || vinNo === void 0 ? void 0 : vinNo.trim())) {
            const textMatchedPool = vinWinnerPool.filter((candidate) => candidate.attachment.foldersModelInfo &&
                candidate.attachment.foldersModelInfo.some((folderModel) => folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
                    !!folderModel.contentType &&
                    vinLikeContentTypes.includes(folderModel.contentType) &&
                    isVehicleInfoTextMatched(folderModel.contentType, folderModel.text)));
            if (textMatchedPool.length) {
                vinWinnerPool = textMatchedPool;
            }
        }
    }
    const winnerCandidates = vinWinnerPool.length
        ? vinWinnerPool
        : vinLikeCandidates;
    let vinLikeWinnerKey = `${winnerCandidates[0].groupIndex}-${winnerCandidates[0].attachmentIndex}`;
    let vinLikeWinnerConfidence = Number.NEGATIVE_INFINITY;
    winnerCandidates.forEach((candidate) => {
        const candidateConfidence = getMaxConfidenceForContentTypes(candidate, vinLikeContentTypes);
        if (candidateConfidence > vinLikeWinnerConfidence) {
            vinLikeWinnerKey = `${candidate.groupIndex}-${candidate.attachmentIndex}`;
            vinLikeWinnerConfidence = candidateConfidence;
        }
    });
    vinLikeCandidates.forEach((candidate) => {
        var _a;
        const candidateKey = `${candidate.groupIndex}-${candidate.attachmentIndex}`;
        if (candidateKey === vinLikeWinnerKey) {
            return;
        }
        const candidateVinLikeContentTypes = ((_a = candidate.attachment.foldersModelInfo) === null || _a === void 0 ? void 0 : _a.filter((folderModel) => folderModel.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
            !!folderModel.contentType &&
            vinLikeContentTypes.includes(folderModel.contentType)).map((folderModel) => folderModel.contentType)) || [];
        if (!candidateVinLikeContentTypes.length) {
            return;
        }
        if (!demoteMap.has(candidateKey)) {
            demoteMap.set(candidateKey, new Set());
        }
        candidateVinLikeContentTypes.forEach((contentType) => {
            demoteMap.get(candidateKey).add(contentType);
        });
    });
};
/**
 * Normalizes a nested album hierarchy based on the selected VIN capture option.
 *
 * Behavior by option:
 * - `VIN`: removes `VIN_STICKER` tiles from `VEHICLE_INFORMATION` albums.
 * - `VIN_STICKER`: removes `VIN` tiles from `VEHICLE_INFORMATION` albums and updates
 *   VIN_STICKER tag labels using `translateFn` when provided.
 * - `undefined` / unsupported option: returns the original hierarchy unchanged.
 *
 * This method preserves the album hierarchy and remaps each album recursively.
 *
 * @param albumsToNormalize Album hierarchy to normalize.
 * @param vinOption Preferred VIN capture option.
 * @param translateFn Optional translator used for VIN_STICKER label text.
 * @returns Normalized album hierarchy.
 */
export const normalizeAlbumsByVinOption = (albumsToNormalize, vinOption, translateFn) => {
    if (isVinOptionUnset(vinOption)) {
        return albumsToNormalize;
    }
    if (vinOption === IntelligentPhotoCaptureVinOption.VIN) {
        return filterVinStickerFromVehicleInformation(albumsToNormalize);
    }
    if (vinOption === IntelligentPhotoCaptureVinOption.VIN_STICKER) {
        return filterVinFromVehicleInformationAndUpdateLabels(albumsToNormalize, translateFn);
    }
    return albumsToNormalize;
};
const filterVinStickerFromVehicleInformation = (albumsToFilter) => {
    const remapAlbumForVin = (album) => {
        const remappedSubAlbums = (album.subAlbums || []).map((subAlbum) => remapAlbumForVin(subAlbum));
        const remappedTiles = album.tiles.filter((tile) => !(album.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
            tile.contentType === IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER));
        return Object.assign(Object.assign({}, album), { tiles: remappedTiles, subAlbums: remappedSubAlbums });
    };
    return albumsToFilter.map((album) => remapAlbumForVin(album));
};
const filterVinFromVehicleInformationAndUpdateLabels = (albumsToFilter, translateFn) => {
    const vinStickerLabel = translateFn
        ? translateFn(PhotoCaptureDownloadArtifactsLabelNames.VIN_STICKER)
        : EMPTY_TEXT;
    const remapAlbum = (album) => {
        const remappedSubAlbums = (album.subAlbums || []).map((subAlbum) => remapAlbum(subAlbum));
        const remappedTiles = album.tiles
            .filter((tile) => !(album.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.VEHICLE_INFORMATION &&
            tile.contentType === IntelligentPhotoCaptureEstimatorContentType.VIN))
            .map((tile) => {
            var _a;
            if (tile.contentType !== IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER || !((_a = tile.tags) === null || _a === void 0 ? void 0 : _a.length)) {
                return tile;
            }
            const tags = [...tile.tags];
            tags[0] = Object.assign(Object.assign({}, tags[0]), { text: vinStickerLabel });
            return Object.assign(Object.assign({}, tile), { tags });
        });
        return Object.assign(Object.assign({}, album), { tiles: remappedTiles, subAlbums: remappedSubAlbums });
    };
    return albumsToFilter.map((album) => remapAlbum(album));
};
//# sourceMappingURL=intelligentPhotoCaptureEstimatorVinOptionUtilities.js.map